import {
  Award,
  BarChart3,
  Building,
  CheckCircle,
  Clock,
  Copy,
  DollarSign,
  Edit,
  GitBranch,
  Link as LinkIcon,
  LogOut,
  Mail,
  Phone,
  TrendingUp,
  User,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import FlexibleInput from '../../components/Input/Input';
import { getEnvironmentConfig } from '../../config';
import { Error, Success } from '../../config/helpers/toast-helper';
import { useAffiliateUserStore } from '../../store/affiliate-user';

// Edit Profile Modal Component
const EditProfileModal = ({ isOpen, onClose, user, onUpdate }) => {
  const { updateProfile, loading } = useAffiliateUserStore();
  const [name, setName] = useState(user?.name || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const formatPhoneNumber = (value) => {
    let cleaned = value.replace(/[^\d+]/g, '');
    if (!cleaned.startsWith('+') && cleaned.length > 0) {
      cleaned = '+' + cleaned;
    }
    return cleaned.slice(0, 16);
  };

  const validateName = (value) => {
    if (!value.trim()) return 'Name is required';
    if (value.trim().length < 2) return 'Name must be at least 2 characters';
    return '';
  };

  const validatePhoneNumber = (value) => {
    if (!value.trim()) return 'Phone number is required';
    const cleanPhone = value.replace(/[^\d+]/g, '');
    if (cleanPhone.length < 10)
      return 'Phone number must be at least 10 digits';
    if (cleanPhone.length > 15)
      return 'Phone number must be less than 15 digits';
    if (!cleanPhone.startsWith('+'))
      return 'Please include country code (e.g., +1)';
    return '';
  };

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  React.useEffect(() => {
    const newErrors = {};
    if (touchedFields.name) newErrors.name = validateName(name);
    if (touchedFields.phoneNumber)
      newErrors.phoneNumber = validatePhoneNumber(phoneNumber);
    setErrors(newErrors);
  }, [name, phoneNumber, touchedFields]);

  const handleSave = async () => {
    const allTouched = { name: true, phoneNumber: true };
    setTouchedFields(allTouched);

    const nameError = validateName(name);
    const phoneError = validatePhoneNumber(phoneNumber);

    if (nameError || phoneError) {
      setErrors({ name: nameError, phoneNumber: phoneError });
      Error('Please fix the form errors before submitting');
      return;
    }

    try {
      const payload = { name: name.trim(), phoneNumber: phoneNumber.trim() };
      const response = await updateProfile(payload);

      if (response?.success !== false) {
        Success('Profile updated successfully!');
        onUpdate(response.user);
        onClose();
      }
    } catch (error) {
      console.error('Profile update error:', error);
      Error(
        error?.response?.data?.message ||
          'Failed to update profile. Please try again.'
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800/90 backdrop-blur-xl rounded-2xl p-6 w-full max-w-md border border-gray-700/50">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">Edit Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <FlexibleInput
            placeholder="Full Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => handleBlur('name')}
            icon={User}
            iconClass="text-green-400"
            error={errors.name}
            touched={touchedFields.name}
            className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
          />

          <FlexibleInput
            placeholder="Phone Number (e.g., +12345678900)"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
            onBlur={() => handleBlur('phoneNumber')}
            icon={Phone}
            iconClass="text-green-400"
            error={errors.phoneNumber}
            touched={touchedFields.phoneNumber}
            className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
          />

          <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
            <div className="flex items-center mb-2">
              <Mail className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-gray-400 text-sm">Email Address</span>
            </div>
            <p className="text-white font-medium">{user?.email}</p>
            <p className="text-gray-500 text-xs mt-1">
              Email cannot be changed
            </p>
          </div>
        </div>

        <div className="flex space-x-3 mt-6">
          <Button
            text="Cancel"
            onClick={onClose}
            variant="outline"
            className="flex-1 bg-gray-700/50 border-gray-600 text-white hover:bg-gray-600 hover:border-gray-500"
          />
          <Button
            text="Save Changes"
            onClick={handleSave}
            loading={loading}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0"
          />
        </div>
      </div>
    </div>
  );
};

// User Progress Badge Component
const UserProgressBadge = ({ user }) => {
  const getProgressStep = (user) => {
    if (!user.emailVerified)
      return { step: 'Email Verification', color: 'yellow', icon: Mail };
    if (user.repositoriesConnected === 0)
      return { step: 'Connect Repository', color: 'blue', icon: GitBranch };
    if (user.organizationsCreated === 0)
      return { step: 'Create Organization', color: 'purple', icon: Building };
    return { step: 'Active User', color: 'green', icon: CheckCircle };
  };

  const progress = getProgressStep(user);
  const colorClasses = {
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
  };

  return (
    <div
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
        colorClasses[progress.color]
      }`}
    >
      <progress.icon className="w-3 h-3 mr-1" />
      {progress.step}
    </div>
  );
};

const Dashboard = () => {
  const {
    user,
    logout,
    referredUsers,
    dashboardStats,
    fetchReferredUsers,
    fetchDashboardStats,
    clearDashboardData,
    loading,
  } = useAffiliateUserStore();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const [isLoadingData, setIsLoadingData] = useState(true);

  React.useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  React.useEffect(() => {
    try {
      const affiliateUserData = localStorage.getItem('affiliateUserData');
      if (affiliateUserData && !user) {
        const userData = JSON.parse(affiliateUserData);
        setCurrentUser(userData);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, [user]);

  // Fetch dashboard data on component mount
  React.useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoadingData(true);
        await Promise.all([fetchReferredUsers(), fetchDashboardStats()]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Don't show dummy data, just show empty state
      } finally {
        setIsLoadingData(false);
      }
    };

    if (currentUser?.id) {
      fetchDashboardData();
    } else {
      setIsLoadingData(false);
    }
  }, [currentUser?.id, fetchReferredUsers, fetchDashboardStats]);

  const handleLogout = () => {
    clearDashboardData();
    logout();
    navigate('/login');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    Success('Copied to clipboard!');
  };

  const handleProfileUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
  };

  // Use real data only
  const displayUsers = referredUsers || [];

  // Calculate stats from real data only
  const stats = dashboardStats || {
    totalReferrals: displayUsers.length,
    activeUsers: displayUsers.filter((u) => u.status === 'active').length,
    pendingUsers: displayUsers.filter((u) => u.status === 'pending').length,
    totalRepositories: displayUsers.reduce(
      (sum, u) => sum + (u.repositoriesConnected || 0),
      0
    ),
    totalOrganizations: displayUsers.reduce(
      (sum, u) => sum + (u.organizationsCreated || 0),
      0
    ),
    verifiedUsers: displayUsers.filter((u) => u.emailVerified).length,
    totalEarnings: 0, // Will come from backend
    monthlyEarnings: 0, // Will come from backend
  };

  // Fix referral link to use correct frontend URL
  const config = getEnvironmentConfig();
  const frontendUrl =
    window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : 'https://app.hikaflow.com';
  const referralLink = `${frontendUrl}/referral/${currentUser?.id}`;

  // Loading state
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto"></div>
          <p className="text-gray-100 mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-xl border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  Partner Dashboard
                </h1>
                <p className="text-gray-400 text-sm">
                  Welcome back, {currentUser?.name}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-red-400 transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="h-[calc(100vh-4rem)] overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 hover:scale-105 transition-transform">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Referrals</p>
                  <p className="text-3xl font-bold text-white">
                    {stats.totalReferrals}
                  </p>
                </div>
                <Users className="w-8 h-8 text-green-400" />
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 hover:scale-105 transition-transform">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Active Users</p>
                  <p className="text-3xl font-bold text-white">
                    {stats.activeUsers}
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-blue-400" />
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 hover:scale-105 transition-transform">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Monthly Earnings</p>
                  <p className="text-3xl font-bold text-white">
                    ${stats.monthlyEarnings}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-yellow-400" />
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 hover:scale-105 transition-transform">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Earnings</p>
                  <p className="text-3xl font-bold text-white">
                    ${stats.totalEarnings}
                  </p>
                </div>
                <Award className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>

          {/* Referral Link */}
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <LinkIcon className="w-5 h-5 mr-2" />
              Your Referral Link
            </h3>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={referralLink}
                readOnly
                className="flex-1 bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-gray-300 text-sm"
              />
              <button
                onClick={() => copyToClipboard(referralLink)}
                className="p-2 bg-green-500 hover:bg-green-600 rounded-lg transition-colors"
              >
                <Copy className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Referred Users Table */}
          <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Referred Users ({displayUsers.length})
            </h3>

            {displayUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-2">No referrals yet</p>
                <p className="text-gray-500 text-sm">
                  Start sharing your referral link to see users here
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700/50">
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        User
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        Email Status
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        Progress
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        Plan
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        Repositories
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        Organizations
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        Joined
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-300">
                        Last Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {user.name}
                              </p>
                              <p className="text-gray-400 text-xs">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            {user.emailVerified ? (
                              <div className="flex items-center text-green-400">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                <span className="text-xs">Verified</span>
                              </div>
                            ) : (
                              <div className="flex items-center text-yellow-400">
                                <Clock className="w-4 h-4 mr-1" />
                                <span className="text-xs">Pending</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <UserProgressBadge user={user} />
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.plan === 'Enterprise'
                                ? 'bg-purple-500/20 text-purple-400'
                                : user.plan === 'Pro'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}
                          >
                            {user.plan}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center text-gray-300">
                            <GitBranch className="w-4 h-4 mr-1" />
                            <span>{user.repositoriesConnected || 0}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center text-gray-300">
                            <Building className="w-4 h-4 mr-1" />
                            <span>{user.organizationsCreated || 0}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-gray-400 text-xs">
                            {new Date(
                              user.signupDate || user.createdAt
                            ).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-gray-400 text-xs">
                            {new Date(
                              user.lastActivity || user.updatedAt
                            ).toLocaleDateString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Additional Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            {/* User Analytics */}
            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                User Analytics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Email Verified</span>
                  <span className="text-green-400 font-medium">
                    {stats.verifiedUsers}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Pending Verification</span>
                  <span className="text-yellow-400 font-medium">
                    {stats.pendingUsers}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Repositories</span>
                  <span className="text-blue-400 font-medium">
                    {stats.totalRepositories}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Organizations</span>
                  <span className="text-purple-400 font-medium">
                    {stats.totalOrganizations}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Connection Rate</span>
                  <span className="text-green-400 font-medium">
                    {stats.totalRepositories > 0
                      ? Math.round(
                          (stats.totalRepositories / stats.totalReferrals) * 100
                        )
                      : 0}
                    %
                  </span>
                </div>
              </div>
            </div>

            {/* Performance Overview */}
            <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Performance Overview
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Conversion Rate</span>
                  <span className="text-green-400 font-medium">
                    {stats.totalReferrals > 0
                      ? Math.round(
                          (stats.activeUsers / stats.totalReferrals) * 100
                        )
                      : 0}
                    %
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    Avg. Repositories per User
                  </span>
                  <span className="text-blue-400 font-medium">
                    {stats.totalReferrals > 0
                      ? (
                          stats.totalRepositories / stats.totalReferrals
                        ).toFixed(1)
                      : 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Monthly Referrals</span>
                  <span className="text-yellow-400 font-medium">
                    {
                      displayUsers.filter((u) => {
                        const signupDate = new Date(
                          u.signupDate || u.createdAt
                        );
                        const now = new Date();
                        return (
                          signupDate.getMonth() === now.getMonth() &&
                          signupDate.getFullYear() === now.getFullYear()
                        );
                      }).length
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom padding for better scrolling */}
          <div className="h-8"></div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={currentUser}
        onUpdate={handleProfileUpdate}
      />
    </div>
  );
};

export default Dashboard;
